import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Plus, Pencil, Trash2, FolderKanban, Calendar, User, FileText, Users, DollarSign, LayoutTemplate, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import PhaseCard from "@/components/projects/PhaseCard";
import PhaseForm from "@/components/projects/PhaseForm";
import ProjectForm from "@/components/projects/ProjectForm";
import TaskForm from "@/components/tasks/TaskForm";
import TasksList from "@/components/tasks/TasksList";
import TimeTracker from "@/components/time/TimeTracker";
import CommentSection from "@/components/collaboration/CommentSection";
import DocumentUpload from "@/components/collaboration/DocumentUpload";
import GanttChart from "@/components/projects/GanttChart";
import MilestoneForm from "@/components/projects/MilestoneForm";
import ProjectReport from "@/components/projects/ProjectReport";
import ProjectDashboard from "@/components/projects/ProjectDashboard";
import ResourceAllocation from "@/components/projects/ResourceAllocation";
import BudgetTracking from "@/components/projects/BudgetTracking";
import ProjectTeamManager from "@/components/projects/ProjectTeamManager";
import TaskTemplateSelector from "@/components/tasks/TaskTemplateSelector";
import ProjectTimeEntries from "@/components/projects/ProjectTimeEntries";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flag, BarChart3, MessageSquare, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  planning: "bg-slate-100 text-slate-700",
  active: "bg-blue-100 text-blue-700",
  on_hold: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-rose-100 text-rose-700",
};

const statusLabels = {
  planning: "Planlegger",
  active: "Aktiv",
  on_hold: "På Vent",
  completed: "Fullført",
  cancelled: "Kansellert",
};

export default function ProjectDetail() {
  const [projectId, setProjectId] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    setProjectId(id);
  }, []);

  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      const projects = await base44.entities.Project.filter({ id: projectId });
      return projects[0];
    },
    enabled: !!projectId,
  });

  const { data: phases = [] } = useQuery({
    queryKey: ["phases", projectId],
    queryFn: () => base44.entities.Phase.filter({ project_id: projectId }, "order"),
    enabled: !!projectId,
  });

  const { data: tasks = [], refetch: refetchTasks } = useQuery({
    queryKey: ["project-tasks", projectId],
    queryFn: () => base44.entities.Task.filter({ project_id: projectId }, "-created_date"),
    enabled: !!projectId,
  });

  const { data: subtasks = [] } = useQuery({
    queryKey: ["project-subtasks", projectId],
    queryFn: async () => {
      const allTasks = await base44.entities.Task.list();
      return allTasks.filter(t => t.parent_task_id && tasks.some(pt => pt.id === t.parent_task_id));
    },
    enabled: !!projectId && tasks.length > 0,
  });

  const { data: milestones = [] } = useQuery({
    queryKey: ["milestones", projectId],
    queryFn: () => base44.entities.Milestone.filter({ project_id: projectId }, "due_date"),
    enabled: !!projectId,
  });

  const { data: timeEntries = [], refetch: refetchTimeEntries } = useQuery({
    queryKey: ["project-time-entries", projectId],
    queryFn: () => base44.entities.TimeEntry.filter({ project_id: projectId }),
    enabled: !!projectId,
    refetchInterval: 5000,
  });

  const { data: comments = [], refetch: refetchComments } = useQuery({
    queryKey: ["project-comments", projectId],
    queryFn: () => base44.entities.Comment.filter({ related_to_type: "project", related_to_id: projectId }, "-created_date"),
    enabled: !!projectId,
    refetchInterval: 5000,
  });

  const { data: teamMembers = [] } = useQuery({
    queryKey: ["project-team", projectId],
    queryFn: async () => {
      const teams = await base44.entities.Team.filter({ related_to_type: "project", related_to_id: projectId });
      if (teams.length === 0) return [];
      const allMembers = await base44.entities.TeamMember.list();
      const teamIds = teams.map(t => t.id);
      return allMembers.filter(m => teamIds.includes(m.team_id));
    },
    enabled: !!projectId,
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["project-invoices", projectId],
    queryFn: () => base44.entities.Invoice.filter({ project_id: projectId }),
    enabled: !!projectId,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Project.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project"] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setShowProjectForm(false);
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: (id) => base44.entities.Project.delete(id),
    onSuccess: () => {
      navigate(createPageUrl("Projects"));
    },
  });

  const createPhaseMutation = useMutation({
    mutationFn: (data) => base44.entities.Phase.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phases"] });
      setShowPhaseForm(false);
      setEditingPhase(null);
    },
  });

  const updatePhaseMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Phase.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phases"] });
      setShowPhaseForm(false);
      setEditingPhase(null);
    },
  });

  const deletePhaseMutation = useMutation({
    mutationFn: (id) => base44.entities.Phase.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phases"] });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => {
      refetchTasks();
      refetchTimeEntries();
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-time-entries"] });
      setShowTaskForm(false);
      setEditingTask(null);
      setSelectedPhaseId(null);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      refetchTasks();
      refetchTimeEntries();
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-time-entries"] });
      setShowTaskForm(false);
      setEditingTask(null);
      setSelectedPhaseId(null);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => {
      refetchTasks();
      refetchTimeEntries();
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-subtasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["project-time-entries"] });
    },
  });

  const createMilestoneMutation = useMutation({
    mutationFn: (data) => base44.entities.Milestone.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones"] });
      setShowMilestoneForm(false);
      setEditingMilestone(null);
    },
  });

  const updateMilestoneMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Milestone.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones"] });
      setShowMilestoneForm(false);
      setEditingMilestone(null);
    },
  });

  const deleteMilestoneMutation = useMutation({
    mutationFn: (id) => base44.entities.Milestone.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["milestones"] });
    },
  });

  const handlePhaseSubmit = (data) => {
    if (editingPhase) {
      updatePhaseMutation.mutate({ id: editingPhase.id, data });
    } else {
      createPhaseMutation.mutate(data);
    }
  };

  const handleTaskSubmit = (data) => {
    const taskData = {
      ...data,
      project_id: projectId,
      project_name: project?.name,
    };
    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, data: taskData });
    } else {
      createTaskMutation.mutate(taskData);
    }
  };

  const handleTaskStatusChange = (task, newStatus) => {
    updateTaskMutation.mutate({
      id: task.id,
      data: { ...task, status: newStatus }
    });
  };

  const handleMilestoneSubmit = (data) => {
    if (editingMilestone) {
      updateMilestoneMutation.mutate({ id: editingMilestone.id, data });
    } else {
      createMilestoneMutation.mutate(data);
    }
  };

  const handleApplyTemplate = async (template) => {
    const { data: user } = await queryClient.fetchQuery({
      queryKey: ["currentUser"],
      queryFn: () => base44.auth.me(),
    });

    const tasksToCreate = template.tasks.map((taskTemplate, index) => ({
      title: taskTemplate.title,
      description: taskTemplate.description || "",
      status: "todo",
      priority: taskTemplate.priority || "medium",
      assigned_to: user.email,
      assigned_to_name: user.full_name,
      estimated_hours: taskTemplate.estimated_hours || 0,
      project_id: projectId,
      project_name: project?.name,
      phase_id: selectedPhaseId || null,
      related_to_type: project.related_to_type || "none",
      related_to_id: project.related_to_id || null,
      related_to_name: project.related_to_name || null,
    }));

    try {
      await base44.entities.Task.bulkCreate(tasksToCreate);
      refetchTasks();
      toast.success(`${tasksToCreate.length} oppgaver opprettet fra mal`);
    } catch (error) {
      toast.error("Feil ved opprettelse av oppgaver fra mal");
    }
  };

  const calculateProgress = () => {
    if (tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === "completed").length;
    return (completed / tasks.length) * 100;
  };

  const calculatePhaseProgress = (phaseId) => {
    const phaseTasks = tasks.filter(t => t.phase_id === phaseId);
    if (phaseTasks.length === 0) return 0;
    const completed = phaseTasks.filter(t => t.status === "completed").length;
    return (completed / phaseTasks.length) * 100;
  };

  const getPhaseTaskCount = (phaseId) => {
    return tasks.filter(t => t.phase_id === phaseId).length;
  };

  const calculatePhaseEstimatedCost = (phaseId) => {
    const phaseTasks = tasks.filter(t => t.phase_id === phaseId);
    return phaseTasks.reduce((total, task) => {
      if (!task.estimated_hours || !task.assigned_to) return total;
      const user = users.find(u => u.email === task.assigned_to);
      const hourlyRate = user?.hourly_rate || 0;
      return total + (task.estimated_hours * hourlyRate);
    }, 0);
  };

  const unassignedTasks = tasks.filter(t => !t.phase_id && !t.parent_task_id);

  if (!project) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(createPageUrl("Projects"))}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Tilbake til Prosjekter
        </Button>

        {/* Project Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-zinc-900" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">{project.name}</h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <Badge className={cn("border-0", statusColors[project.status])}>
                    {statusLabels[project.status]}
                  </Badge>
                  {project.owner_name && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <User className="w-4 h-4" />
                      <span>{project.owner_name}</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowTeamManager(true)}
                    className="flex items-center gap-1 text-sm text-slate-500 hover:text-zinc-900 transition-colors"
                  >
                    <Users className="w-4 h-4" />
                    <span>{teamMembers.length} teammedlemmer</span>
                  </button>
                  {project.end_date && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <Calendar className="w-4 h-4" />
                      <span>{format(new Date(project.end_date), "MMM d, yyyy")}</span>
                    </div>
                  )}
                </div>
                </div>
                </div>
                <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowReport(true)}>
                <FileText className="w-4 h-4 mr-2" />
                Generer Rapport
                </Button>
                <Button variant="outline" size="icon" onClick={() => setShowProjectForm(true)}>
                <Pencil className="w-4 h-4" />
                </Button>
                <Button 
                variant="outline" 
                size="icon" 
                className="text-rose-600 hover:text-rose-700"
                onClick={() => {
                  if (confirm("Er du sikker på at du vil slette dette prosjektet?")) {
                    deleteProjectMutation.mutate(projectId);
                  }
                }}
                >
                <Trash2 className="w-4 h-4" />
                </Button>
                </div>
          </div>

          {project.description && (
            <p className="text-slate-600 mb-4">{project.description}</p>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>Prosjektfremdrift</span>
              <span className="font-medium">{Math.round(calculateProgress())}%</span>
            </div>
            <Progress value={calculateProgress()} className="h-2" />
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>{phases.length} faser</span>
              <span>{tasks.length} oppgaver</span>
              <span>{tasks.filter(t => t.status === "completed").length} fullført</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Fakturert</div>
              <div className="text-lg font-semibold text-green-600">
                {invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString('nb-NO')} kr
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Til fakturering</div>
              <div className="text-lg font-semibold text-orange-600">
                {invoices.filter(inv => inv.status === 'draft' || inv.status === 'sent').reduce((sum, inv) => sum + inv.total_amount, 0).toLocaleString('nb-NO')} kr
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Totalt timer</div>
              <div className="text-lg font-semibold text-slate-900">
                {(timeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0) / 60).toFixed(1)} t
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="dashboard">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="overview">Oversikt</TabsTrigger>
            <TabsTrigger value="gantt">
              <BarChart3 className="w-4 h-4 mr-2" />
              Gantt
            </TabsTrigger>
            <TabsTrigger value="timeentries">
              <Clock className="w-4 h-4 mr-2" />
              Timer
            </TabsTrigger>
            <TabsTrigger value="resources">
              <Users className="w-4 h-4 mr-2" />
              Ressurser
            </TabsTrigger>
            <TabsTrigger value="budget">
              <DollarSign className="w-4 h-4 mr-2" />
              Budsjett
            </TabsTrigger>
            <TabsTrigger value="milestones">
              <Flag className="w-4 h-4 mr-2" />
              Milepæler
            </TabsTrigger>
            <TabsTrigger value="collaboration">
              <MessageSquare className="w-4 h-4 mr-2" />
              Samarbeid
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6 mt-6">
            <ProjectDashboard
              project={project}
              tasks={tasks}
              milestones={milestones}
              timeEntries={timeEntries}
              teamMembers={teamMembers}
              comments={comments}
            />
          </TabsContent>

          <TabsContent value="overview" className="space-y-6 mt-6">
            <TimeTracker 
              projectId={projectId}
              projectName={project.name}
              relatedToType={project.related_to_type}
              relatedToId={project.related_to_id}
              relatedToName={project.related_to_name}
            />

          </TabsContent>

          <TabsContent value="gantt" className="mt-6">
            <GanttChart 
              project={project}
              phases={phases}
              tasks={tasks}
              milestones={milestones}
            />
          </TabsContent>

          <TabsContent value="timeentries" className="mt-6">
            <ProjectTimeEntries
              timeEntries={timeEntries}
              tasks={tasks}
              users={users}
            />
          </TabsContent>

          <TabsContent value="resources" className="mt-6">
            <ResourceAllocation
              project={project}
              tasks={tasks}
              teamMembers={teamMembers}
              timeEntries={timeEntries}
            />
          </TabsContent>

          <TabsContent value="budget" className="mt-6">
            <BudgetTracking
              project={project}
              tasks={tasks}
              timeEntries={timeEntries}
              invoices={invoices}
              onUpdateBudget={() => setShowProjectForm(true)}
            />
          </TabsContent>

          <TabsContent value="milestones" className="space-y-6 mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-slate-900">Milepæler</h2>
              <Button onClick={() => setShowMilestoneForm(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" /> Ny Milepæl
              </Button>
            </div>

            {milestones.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <p className="text-slate-500 mb-4">Ingen milepæler ennå</p>
                <Button onClick={() => setShowMilestoneForm(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Opprett Første Milepæl
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {milestones.map((milestone) => (
                  <Card key={milestone.id} className="p-4 bg-white border-0 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Flag className={milestone.status === "completed" ? "w-5 h-5 text-emerald-600" : "w-5 h-5 text-amber-600"} />
                        <div>
                          <h4 className="font-semibold text-slate-900">{milestone.name}</h4>
                          {milestone.description && (
                            <p className="text-sm text-slate-600 mt-1">{milestone.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={milestone.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                              {milestone.status === "completed" ? "Fullført" : "Pågår"}
                            </Badge>
                            <span className="text-sm text-slate-500">
                              {format(new Date(milestone.due_date), "dd.MM.yyyy")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => {
                            setEditingMilestone(milestone);
                            setShowMilestoneForm(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm("Er du sikker på at du vil slette denne milepælen?")) {
                              deleteMilestoneMutation.mutate(milestone.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="collaboration" className="space-y-6 mt-6">
            <CommentSection 
              relatedToType="project"
              relatedToId={projectId}
              relatedToName={project.name}
              projectId={projectId}
              projectName={project.name}
            />
            <DocumentUpload
              relatedToType="project"
              relatedToId={projectId}
              relatedToName={project.name}
            />
          </TabsContent>
        </Tabs>

        {/* Phases Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900">Faser</h2>
            <Button onClick={() => setShowPhaseForm(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Ny Fase
            </Button>
          </div>

          {phases.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center">
              <p className="text-slate-500 mb-4">Ingen faser ennå</p>
              <Button onClick={() => setShowPhaseForm(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" /> Opprett Første Fase
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {phases.map(phase => (
                <PhaseCard
                  key={phase.id}
                  phase={phase}
                  progress={calculatePhaseProgress(phase.id)}
                  taskCount={getPhaseTaskCount(phase.id)}
                  estimatedCost={calculatePhaseEstimatedCost(phase.id)}
                  onEdit={() => {
                    setEditingPhase(phase);
                    setShowPhaseForm(true);
                  }}
                  onDelete={() => {
                    if (confirm("Er du sikker på at du vil slette denne fasen?")) {
                      deletePhaseMutation.mutate(phase.id);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Tasks Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900">Oppgaver</h2>
            <div className="flex gap-2">
              <Button onClick={() => setShowTemplateSelector(true)} variant="outline">
                <LayoutTemplate className="w-4 h-4 mr-2" /> Bruk Mal
              </Button>
              <Button onClick={() => setShowTaskForm(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" /> Ny Oppgave
              </Button>
            </div>
          </div>

          {phases.map(phase => {
            const phaseTasks = tasks.filter(t => t.phase_id === phase.id && !t.parent_task_id);
            if (phaseTasks.length === 0) return null;

            return (
              <div key={phase.id} className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-slate-900">{phase.name}</h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setSelectedPhaseId(phase.id);
                      setShowTaskForm(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Legg til Oppgave
                  </Button>
                </div>
                <TasksList
                  tasks={phaseTasks}
                  subtasks={subtasks}
                  allTasks={tasks}
                  timeEntries={timeEntries}
                  onEdit={(task) => {
                    setEditingTask(task);
                    setShowTaskForm(true);
                  }}
                  onDelete={(id) => deleteTaskMutation.mutate(id)}
                  onStatusChange={handleTaskStatusChange}
                  onAddSubtask={(task) => {
                    setEditingTask({ parent_task_id: task.id });
                    setShowTaskForm(true);
                  }}
                />
              </div>
            );
          })}

          {unassignedTasks.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-slate-900 mb-3">Uten Fase</h3>
              <TasksList
                tasks={unassignedTasks}
                subtasks={subtasks}
                allTasks={tasks}
                timeEntries={timeEntries}
                onEdit={(task) => {
                  setEditingTask(task);
                  setShowTaskForm(true);
                }}
                onDelete={(id) => deleteTaskMutation.mutate(id)}
                onStatusChange={handleTaskStatusChange}
                onAddSubtask={(task) => {
                  setEditingTask({ parent_task_id: task.id });
                  setShowTaskForm(true);
                }}
              />
            </div>
          )}

          {tasks.length === 0 && (
            <div className="bg-white rounded-xl p-8 text-center">
              <p className="text-slate-500 mb-4">Ingen oppgaver ennå</p>
              <Button onClick={() => setShowTaskForm(true)} variant="outline">
                <Plus className="w-4 h-4 mr-2" /> Opprett Første Oppgave
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Forms */}
      {showProjectForm && (
        <ProjectForm
          project={project}
          onSubmit={(data) => updateProjectMutation.mutate({ id: projectId, data })}
          onClose={() => setShowProjectForm(false)}
          isLoading={updateProjectMutation.isPending}
        />
      )}

      {showPhaseForm && (
        <PhaseForm
          phase={editingPhase}
          projectId={projectId}
          projectName={project.name}
          nextOrder={phases.length + 1}
          onSubmit={handlePhaseSubmit}
          onClose={() => {
            setShowPhaseForm(false);
            setEditingPhase(null);
          }}
          isLoading={createPhaseMutation.isPending || updatePhaseMutation.isPending}
        />
      )}

      {showTaskForm && (
        <TaskForm
          task={editingTask}
          onSubmit={handleTaskSubmit}
          onClose={() => {
            setShowTaskForm(false);
            setEditingTask(null);
            setSelectedPhaseId(null);
          }}
          isLoading={createTaskMutation.isPending || updateTaskMutation.isPending}
          projectId={projectId}
          projectName={project.name}
          phaseId={selectedPhaseId}
          phases={phases}
        />
      )}

      {showMilestoneForm && (
        <MilestoneForm
          milestone={editingMilestone}
          projectId={projectId}
          projectName={project.name}
          onSubmit={handleMilestoneSubmit}
          onClose={() => {
            setShowMilestoneForm(false);
            setEditingMilestone(null);
          }}
          isLoading={createMilestoneMutation.isPending || updateMilestoneMutation.isPending}
          />
          )}

          {showReport && (
          <ProjectReport
          project={project}
          onClose={() => setShowReport(false)}
          />
          )}

          {showTeamManager && (
            <ProjectTeamManager
              projectId={projectId}
              projectName={project.name}
              onClose={() => setShowTeamManager(false)}
            />
          )}

          {showTemplateSelector && (
            <TaskTemplateSelector
              onApplyTemplate={handleApplyTemplate}
              onClose={() => setShowTemplateSelector(false)}
            />
          )}
          </div>
          );
          }